import { navigateTo } from '#imports'
import type { RouteLocationNormalized, RouteRecordRaw } from 'vue-router'

import type { Bcp47Locale } from '@backmarket/http-api/src/standards/Locale'

import { PAGE_NAME_BY_PLP_UUID } from '~/scopes/cms/service/content-service.config'
import { generateWebviewRoutes } from '~/scopes/webviews/generateWebviewRoutes'
import redirectIfWebview from '~/scopes/webviews/middleware/redirectIfWebview'

import { ROUTES as PRODUCT_LIST_ROUTES } from './route-names'

export default ({ locale }: { locale: Bcp47Locale }): RouteRecordRaw[] => {
  const availableUniverseUUID = PAGE_NAME_BY_PLP_UUID[locale]
  const universeRegex = availableUniverseUUID
    ? Object.keys(availableUniverseUUID).join('|')
    : '__no_universe_page__'

  const getUuidParam = (to: RouteLocationNormalized) =>
    Array.isArray(to.params.uuid) ? to.params.uuid[0] : to.params.uuid

  const isUniverseUuid = (uuid: string) =>
    Boolean(availableUniverseUUID && uuid in availableUniverseUUID)

  const universeRoute: RouteRecordRaw = {
    name: PRODUCT_LIST_ROUTES.UNIVERSE,
    component: () => import('~/scopes/cms/Landing.vue'),
    path: `l/:slugV2/:uuid(${universeRegex})`,
    meta: {
      owners: ['bot-squad-search-and-recommendation-front'],
      // Named-route navigation bypasses the path regex, so an unknown UUID would
      // wrongly render Landing.vue. Fall back to the PLP — inverse of the PLP guard
      // below, so the two conditions are mutually exclusive and cannot loop.
      middleware: [
        redirectIfWebview,
        (to) =>
          isUniverseUuid(getUuidParam(to))
            ? undefined
            : navigateTo({
                name: PRODUCT_LIST_ROUTES.PRODUCT_LIST,
                params: to.params,
                query: to.query,
                hash: to.hash,
              }),
      ],
      cache: 'public, max-age=300, stale-while-revalidate=600',
      tracking: { contentGroup: 'Category Landing Page' },
    },
  }

  const plpRoute: RouteRecordRaw = {
    path: 'l/:slugV2/:uuid',
    name: PRODUCT_LIST_ROUTES.PRODUCT_LIST,
    meta: {
      owners: ['bot-squad-search-and-recommendation-front'],
      middleware: (to) =>
        isUniverseUuid(getUuidParam(to))
          ? navigateTo({
              name: PRODUCT_LIST_ROUTES.UNIVERSE,
              params: to.params,
              query: to.query,
              hash: to.hash,
            })
          : undefined,
      cache: 'public, max-age=300, stale-while-revalidate=600',
      tracking: { contentGroup: 'Product Listing Page' },
    },
    component: () =>
      import('~/scopes/product-list/pages/ProductListPage/ProductListPage.vue'),
  }

  const sellerProductListRoute: RouteRecordRaw = {
    path: 's/:merchantName/:uuid',
    name: PRODUCT_LIST_ROUTES.SELLER_PRODUCT_LIST,
    meta: {
      owners: ['bot-squad-search-and-recommendation-front'],
      middleware: [redirectIfWebview],
      cache: 'public, max-age=300, stale-while-revalidate=600',
      tracking: { contentGroup: 'Marketplace' },
    },
    component: () =>
      import('~/scopes/product-list/pages/SellerPage/SellerPage.vue'),
  }

  return [
    {
      path: 'search',
      name: PRODUCT_LIST_ROUTES.SEARCH_RESULTS,
      meta: {
        owners: ['bot-squad-search-and-recommendation-front'],
        tracking: { contentGroup: 'Search' },
      },
      component: () =>
        import('~/scopes/product-list/pages/SearchResultPage/SearchResultPage.vue'),
    },
    sellerProductListRoute,
    plpRoute,
    universeRoute,
    ...generateWebviewRoutes([sellerProductListRoute, universeRoute]),
  ]
}
